import React, { useState, createContext, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Header from './components/header';
import Footer from './components/footer';
import config from './config';
import Map from './components/map';
import Home from './components/home';
import HomeDesktop from './components/home-desktop';
import List from './components/list';
import PoiDetails from './components/poidetails';
import QRScanner from './components/QRScanner';
import ErrorPage from './components/ErrorPage';
import Search from './components/search';
import MapList from './components/maplist';
import RoutesList from './components/RoutesList';
import RouteMap from './components/RouteMap';
import PolicyPopup from './components/PolicyPopup';
import PasswordGate from './components/PasswordGate';
import Analytics from './components/Analytics';
import Extra from './components/Extra';
import Wall from './components/wall';
import InstallPrompt from './components/InstallPrompt';
import IOSInstallPrompt from './components/IOSInstallPrompt';
import PWADebug from './components/PWADebug';
import { testPWA } from './utils/pwaUtils';
import AppMeta from './components/AppMeta';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { authService } from './services';

export const CurrentPoiContext = createContext();
export const DataContext = createContext();
export const TTSContext = createContext();
export const NavigationHistoryContext = createContext();

// Assets dinamici dal config
const getAssets = () => {
  return {
    headerImage: config.assets.getHeaderImage(),
    logotype: config.assets.logo // Fallback per logotype
  };
};

// Funzione per controllare se il dispositivo è mobile
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Funzione per determinare il menu attivo basato sul percorso corrente e la storia
const getActiveMenu = (currentPath, history) => {
  // Se siamo in una pagina POI, guardiamo da dove veniamo
  if (currentPath.startsWith('/poi/')) {
    // Cerchiamo nella storia di navigazione la pagina genitore
    for (let i = history.length - 1; i >= 0; i--) {
      const path = history[i];
      if (path.startsWith('/wall')) return '/wall';
      if (path.startsWith('/maplist') || path.startsWith('/map') || path.startsWith('/list')) return '/maplist';
      if (path.startsWith('/routeslist') || path.startsWith('/routes/')) return '/routeslist';
      if (path.startsWith('/extra')) return '/extra';
      if (path.startsWith('/qr-scanner')) return '/qr-scanner';
    }
    
    // Fallback per accessi diretti: priorità maplist > wall > routeslist
    if (config.components.map) return '/maplist';
    if (config.components.wall) return '/wall';
    if (config.components.routes) return '/routeslist';
    return '/home';
  }
  
  // Se siamo in una mappa di percorso, il menu è routeslist
  if (currentPath.startsWith('/routes/')) return '/routeslist';
  
  // Per tutte le altre pagine, mappiamo direttamente
  const menuMap = {
    '/wall': '/wall',
    '/maplist': '/maplist',
    '/map': '/maplist',
    '/list': '/maplist',
    '/routeslist': '/routeslist',
    '/home': '/home',
    '/extra': '/extra',
    '/qr-scanner': '/qr-scanner'
  };
  
  return menuMap[currentPath] || '/home';
};

function AppContent() {
  const [error, setError] = useState(null);
  const [currentPoi, setCurrentPoi] = useState(null);
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Gestione dello storico di navigazione
  useEffect(() => {
    const history = JSON.parse(sessionStorage.getItem('pagesHistory') || '[]');
    
    // Aggiungiamo la nuova pagina se diversa dall'ultima
    if (history.length === 0 || history[history.length - 1] !== location.pathname) {
      // Manteniamo le ultime 5 pagine
      if (history.length >= 5) {
        history.shift();
      }
      history.push(location.pathname);
      sessionStorage.setItem('pagesHistory', JSON.stringify(history));
    }
  }, [location]);

  // Redirect desktop/mobile
  useEffect(() => {
    if (!isMobileDevice() && location.pathname !== '/desktop') {
      navigate('/desktop');
    }
  }, [navigate, location]);

  // Reset errori e POI corrente
  useEffect(() => {
    if (location.pathname !== '/error') {
      setError(null);
    }
    
    if (!location.pathname.startsWith('/poi/')) {
      setCurrentPoi(null);
    }
  }, [location]);

  const handleError = (message) => {
    setError(message);
    navigate('/error');
  };

  // Gestione TTS
  const ttsContextValue = {
    isTTSPlaying,
    startTTS: () => setIsTTSPlaying(true),
    stopTTS: () => setIsTTSPlaying(false)
  };

  // Gestione Navigation History con funzione per ottenere il menu attivo
  const navigationHistoryValue = {
    getHistory: () => JSON.parse(sessionStorage.getItem('pagesHistory') || '[]'),
    updateHistory: (newHistory) => sessionStorage.setItem('pagesHistory', JSON.stringify(newHistory)),
    getActiveMenu: () => {
      const history = JSON.parse(sessionStorage.getItem('pagesHistory') || '[]');
      return getActiveMenu(location.pathname, history);
    }
  };

  useEffect(() => {
    // Inizializza l'autenticazione all'avvio dell'app
    const initAuth = async () => {
      try {
        await authService.getToken();
      } catch (error) {
        console.error('Errore durante l\'inizializzazione dell\'autenticazione:', error);
      }
    };

    initAuth();
  }, []);

  return (
    <CurrentPoiContext.Provider value={{ currentPoi, setCurrentPoi }}>
      <TTSContext.Provider value={ttsContextValue}>
        <NavigationHistoryContext.Provider value={navigationHistoryValue}>
          <div className="App">
            {location.pathname !== '/desktop' && <Header logo={getAssets().headerImage} />}
            <Routes>
              <Route path="/desktop" element={<HomeDesktop />} />
              <Route path="/home" element={<Home logotype={getAssets().logotype} />} />
              {config.components.wall && <Route path="/wall" element={<Wall />} />}
              <Route path="/map" element={<Map />} />
              <Route path="/list" element={<List />} />
              <Route path="/maplist" element={<MapList />} /> 
              <Route path="/routeslist" element={<RoutesList />} /> 
              <Route path="/routes/:id" element={<RouteMap />} />
              <Route path="/poi/:nid" element={<PoiDetails />} />
              <Route path="/qr-scanner" element={<QRScanner onError={handleError} />} />
              <Route path="/error" element={<ErrorPage error={error} />} />
              <Route path="/search" element={<Search />} />
              <Route path="/policy" element={<PolicyPopup />} />
              <Route path="/extra" element={<Extra />} />
              <Route path="/" element={<Navigate replace to={isMobileDevice() ? '/home' : '/desktop'} />} />
            </Routes>
            {location.pathname !== '/desktop' && <Footer />}
          </div>
        </NavigationHistoryContext.Provider>
      </TTSContext.Provider>
    </CurrentPoiContext.Provider>
  );
}

function App() {
  useEffect(() => {
    // Debug PWA in modalità sviluppo
    if (process.env.NODE_ENV === 'development') {
      // Ritarda il test per permettere al service worker di registrarsi
      setTimeout(() => {
        testPWA();
      }, 2000);
    }
  }, []);

  return (
    <>
      <AppMeta />
      <Analytics />
      <InstallPrompt />
      <IOSInstallPrompt />
      <PWADebug />
      <PasswordGate>
        <Router basename={config.basePath}>
          <AppContent />
        </Router>
      </PasswordGate>
    </>
  );
}

export default App;